import {useMemo, useRef} from 'react';
import {useInfiniteQuery, useQuery} from '@tanstack/react-query';

import {SearchState, useSearchState} from '../../context/SearchContext';
import apiService from '../../transport/apiService';
import type {OrderLine} from '../../transport/apiServiceResponse';

import {buildSearchQueryKey, flattenFacets, searchKeys} from './keys';
import {mergeSpecialsLines} from './mergeSpecialsLines';

// SHORTCUT: a specials list reads one page of 200 member lines per tier; 200 is the server's limit.
// Lines past the cap appear only if they are specials. Upgrade: page the member sets.
const MEMBER_SET_PAGE_SIZE = 200;

type SortOptionId = Parameters<typeof apiService.getDepartmentLines>[0]['sort_option_id'];
type MemberSetResponse = {data: OrderLine[]};

// Filter, price range, keyword and sort apply to the specials request and the member sets alike.
const sharedParams = (state: SearchState) => ({
  keywords: state.query || undefined,
  department_id: state.departmentId ?? undefined,
  sort_option_id: state.sortOptionId as SortOptionId,
  facets: flattenFacets(state.activeFacets),
  // Price range — previously omitted despite being in the query key,
  // causing the price filter UI to silently do nothing.
  sell_price_cents_minimum: state.priceRange ? state.priceRange.min * 100 : undefined,
  sell_price_cents_maximum: state.priceRange ? state.priceRange.max * 100 : undefined,
});

interface UseSearchLinesOptions {
  /**
   * When true, previous query data is shown while a new query fetches, but
   * only within the same department: smooth sort/filter/keyword transitions
   * in RangeProductListScreen, a loading state on a department switch.
   * Disable for SearchScreen where rapid keyword changes cause stale
   * data races between isFetching and isPlaceholderData timing.
   */
  keepPrevious?: boolean;
  /** DIS-2053: with `state.specials`, also read `member_price=private`. Only for an entitled shopper. */
  includePrivate?: boolean;
}

function useMemberSet(
  state: SearchState,
  memberPrice: 'public' | 'private',
  enabled: boolean,
  keepPrevious: boolean,
) {
  return useQuery({
    queryKey: searchKeys.memberLines(state.storeId, buildSearchQueryKey(state), memberPrice),
    queryFn: () =>
      apiService.getDepartmentLines(
        {
          ...sharedParams(state),
          member_price: memberPrice,
          page: 1,
          page_size: MEMBER_SET_PAGE_SIZE,
        },
        {unscoped: true},
      ) as unknown as Promise<MemberSetResponse>,
    enabled,
    // A retrying set would stay pending and hold back the specials list.
    retry: false,
    // A refetch could add a member line above tiles the shopper has seen.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData, previousQuery) =>
      keepPrevious && previousQuery?.queryKey[3].department_id === state.departmentId
        ? previousData
        : undefined,
  });
}

export function useSearchLines({
  keepPrevious = true,
  includePrivate = false,
}: UseSearchLinesOptions = {}) {
  const state = useSearchState();
  const queryKey = buildSearchQueryKey(state);

  const query = useInfiniteQuery({
    queryKey: searchKeys.lines(state.storeId, queryKey),
    queryFn: async ({pageParam = 1}) => {
      const response = await apiService.getDepartmentLines(
        {
          ...sharedParams(state),
          specials: state.specials || undefined,
          page: pageParam,
          page_size: 48,
        },
        // DIS-2050: the global `department` header overrides the query params and can be stale.
        {unscoped: true},
      );
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage?.meta) return undefined;
      const {page, page_count} = lastPage.meta;
      return page < page_count ? page + 1 : undefined;
    },
    // storeId gate prevents queries firing before store selection.
    // placeholderData is safe across store switches because SET_STORE_ID
    // resets query/departmentId atomically, disabling the query before
    // the key changes — so no cross-store placeholder flash occurs.
    enabled: !!state.storeId && (!!state.departmentId || state.specials || !!state.query),
    placeholderData: (previousData, previousQuery) =>
      keepPrevious && previousQuery?.queryKey[3].department_id === state.departmentId
        ? previousData
        : undefined,
  });

  // DIS-2053: `specials=true` misses member-price lines, so a specials list also reads the sets.
  const wantMembers = !!state.storeId && state.specials;
  const publicSet = useMemberSet(state, 'public', wantMembers, keepPrevious);
  const privateSet = useMemberSet(state, 'private', wantMembers && includePrivate, keepPrevious);
  const memberSets = wantMembers ? (includePrivate ? [publicSet, privateSet] : [publicSet]) : [];

  const pages = query.data?.pages;
  const {hasNextPage} = query;
  const publicLines = publicSet.data?.data;
  // A cached private set outlives the entitlement, so read it only while entitled.
  const privateLines = includePrivate ? privateSet.data?.data : undefined;
  // Measured 2026-11-04: a shop whose sort options omit a sort answers 200 under `top_products` and
  // says so in `meta.sort_option_id`. The list then has no price order to merge into.
  const firstPage = pages?.[0] as unknown as {meta?: {sort_option_id?: string}} | undefined;
  const servedSort = firstPage?.meta?.sort_option_id ?? state.sortOptionId;
  const merged = useMemo(() => {
    const specials: OrderLine[] = pages?.flatMap(p => p.data) ?? [];
    return wantMembers
      ? mergeSpecialsLines({
          specials,
          publicSet: publicLines,
          privateSet: privateLines,
          sortOptionId: servedSort,
          specialsHasNextPage: hasNextPage,
        })
      : specials;
  }, [pages, wantMembers, publicLines, privateLines, servedSort, hasNextPage]);

  const isLoading = query.isLoading || memberSets.some(set => set.isLoading);
  const isPlaceholder = query.isPlaceholderData || memberSets.some(set => set.isPlaceholderData);
  // Placeholder answers carry the previous sort and can differ per set, so they cannot be merged.
  // Keep the previous merged list until every set has answered.
  const settledLines = useRef<OrderLine[]>([]);
  if (!isPlaceholder) settledLines.current = merged;
  let lines = merged;
  if (wantMembers) {
    // A department switch has no placeholder, so `isLoading` must not show the old list.
    lines = isLoading ? [] : settledLines.current;
  }

  return {
    ...query,
    lines,
    isLoading,
    isFetching: query.isFetching || memberSets.some(set => set.isFetching),
  };
}
